import { asInstanceOf, EmptyExec, isNull, Notifier, Option, tryCatch, UUID } from "@opendaw/lib-std";
import { ProjectMetaBox } from "@opendaw/studio-boxes";
import { ProjectMeta } from "./ProjectMeta";
import { Workers } from "../Workers";
import { ProjectPaths } from "./ProjectPaths";
export class ProjectProfile {
    #uuid;
    #project;
    #meta;
    #cover;
    #metaUpdated;
    #coverUpdated;
    #coverIdUpdated;
    #metaBox;
    #saved;
    #hasChanges = false;
    #applyingLocal = false;
    constructor(uuid, project, meta, cover, hasBeenSaved = false) {
        this.#uuid = uuid;
        this.#project = project;
        this.#meta = meta;
        this.#cover = cover;
        this.#saved = hasBeenSaved;
        this.#metaUpdated = new Notifier();
        this.#coverUpdated = new Notifier();
        this.#coverIdUpdated = new Notifier();
        const existing = project.rootBox.projectMeta.targetVertex;
        if (existing.nonEmpty()) {
            this.#metaBox = asInstanceOf(existing.unwrap(), ProjectMetaBox);
            this.#readMeta();
        }
        else {
            this.#metaBox = this.#createMetaBox();
        }
        project.own(project.boxGraph.subscribeToAllUpdates({ onUpdate: update => this.#onBoxUpdate(update) }));
    }
    get uuid() { return this.#uuid; }
    get project() { return this.#project; }
    get meta() { return this.#meta; }
    get cover() { return this.#cover; }
    get coverId() { return this.#metaBox.coverId.getValue(); }
    async save() {
        this.updateModifyDate();
        this.#project.editing.mark();
        return this.#saved
            ? ProjectProfile.#writeFiles(this).then(() => {
                this.#hasChanges = false;
                this.#project.editing.markSaved();
            })
            : Promise.reject("Project has not been saved");
    }
    async saveAs(meta) {
        Object.assign(this.meta, meta);
        this.updateModifyDate();
        this.#runLocal(() => this.#writeMetaFields(this.#metaBox));
        if (this.#saved) {
            // Copy project
            const uuid = UUID.generate();
            const project = this.project.copy();
            const meta = ProjectMeta.copy(this.meta);
            const profile = new ProjectProfile(uuid, project, meta, this.#cover, true);
            await ProjectProfile.#writeFiles(profile);
            return Option.wrap(profile);
        }
        else {
            this.#project.editing.mark();
            return ProjectProfile.#writeFiles(this).then(() => {
                this.#saved = true;
                this.#hasChanges = false;
                this.#project.editing.markSaved();
                this.#metaUpdated.notify(this.meta);
                return Option.None;
            });
        }
    }
    saved() { return this.#saved; }
    hasUnsavedChanges() { return this.#project.editing.hasUnsavedChanges() || this.#hasChanges; }
    subscribeMetaData(observer) {
        return this.#metaUpdated.subscribe(observer);
    }
    subscribeCover(observer) {
        return this.#coverUpdated.subscribe(observer);
    }
    /** Notifies whenever the shared cover-id changes (a peer set a different cover). */
    subscribeCoverId(observer) {
        return this.#coverIdUpdated.subscribe(observer);
    }
    updateCover(cover) {
        const next = cover.unwrapOrNull();
        if (next === this.#cover.unwrapOrNull()) {
            return;
        }
        this.#cover = isNull(next) ? Option.None : Option.wrap(next);
        this.#hasChanges = true;
        const coverId = isNull(next) ? "" : UUID.toString(UUID.generate());
        this.#runLocal(() => this.#metaBox.coverId.setValue(coverId));
    }
    /** Stores cover bytes fetched over P2P for the current cover-id (does not change the shared id). */
    setFetchedCover(cover) {
        this.#cover = Option.wrap(cover);
        this.#coverUpdated.notify(this.#cover);
    }
    updateMetaData(key, value) {
        if (this.meta[key] === value) {
            return;
        }
        this.meta[key] = value;
        this.#hasChanges = true;
        if (key !== "radioToken") {
            this.#runLocal(() => this.#writeMetaField(this.#metaBox, key));
        }
        this.#metaUpdated.notify(this.meta);
    }
    updateModifyDate() {
        this.meta.modified = new Date().toISOString();
        this.#runLocal(() => this.#metaBox.modified.setValue(this.meta.modified));
    }
    copyForUpload() {
        const meta = ProjectMeta.copy(this.meta);
        delete meta.radioToken; // we do not expose this
        return new ProjectProfile(this.uuid, this.project, meta, this.cover);
    }
    toString() {
        return `{uuid: ${UUID.toString(this.uuid)}, meta: ${JSON.stringify(this.meta)}}`;
    }
    #createMetaBox() {
        const { rootBox, boxGraph } = this.#project;
        return this.#runLocal(() => {
            const box = ProjectMetaBox.create(boxGraph, UUID.generate());
            rootBox.projectMeta.refer(box);
            this.#writeMetaFields(box);
            box.coverId.setValue(this.#cover.isEmpty() ? "" : UUID.toString(UUID.generate()));
            return box;
        });
    }
    #runLocal(procedure) {
        const { boxGraph } = this.#project;
        this.#applyingLocal = true;
        let result;
        if (boxGraph.inTransaction()) {
            result = procedure();
        }
        else {
            boxGraph.beginTransaction();
            result = procedure();
            boxGraph.endTransaction();
        }
        this.#applyingLocal = false;
        return result;
    }
    #onBoxUpdate(update) {
        if (this.#applyingLocal) {
            return;
        }
        if (update.type !== "primitive" && update.type !== "pointer") {
            return;
        }
        if (!UUID.equals(update.address.uuid, this.#metaBox.address.uuid)) {
            return;
        }
        const { fieldKeys } = update.address;
        if (fieldKeys[fieldKeys.length - 1] === this.#metaBox.coverId.address.fieldKeys[0]) {
            this.#cover = Option.None;
            this.#coverUpdated.notify(this.#cover);
            this.#coverIdUpdated.notify(this.#metaBox.coverId.getValue());
        }
        else {
            this.#readMeta();
            this.#metaUpdated.notify(this.#meta);
        }
    }
    #readMeta() {
        const box = this.#metaBox;
        const meta = this.#meta;
        meta.name = box.projectName.getValue();
        meta.artist = box.artist.getValue();
        meta.description = box.description.getValue();
        meta.tags = this.#parseTags(box.tagList.getValue());
        const notepad = box.notepad.getValue();
        meta.notepad = notepad.length === 0 ? undefined : notepad;
        meta.created = box.created.getValue();
        meta.modified = box.modified.getValue();
    }
    #writeMetaFields(box) {
        const meta = this.#meta;
        box.projectName.setValue(meta.name);
        box.artist.setValue(meta.artist);
        box.description.setValue(meta.description);
        box.tagList.setValue(JSON.stringify(meta.tags));
        box.notepad.setValue(meta.notepad ?? "");
        box.created.setValue(meta.created);
        box.modified.setValue(meta.modified);
    }
    #writeMetaField(box, key) {
        const meta = this.#meta;
        switch (key) {
            case "name": return box.projectName.setValue(meta.name);
            case "artist": return box.artist.setValue(meta.artist);
            case "description": return box.description.setValue(meta.description);
            case "tags": return box.tagList.setValue(JSON.stringify(meta.tags));
            case "notepad": return box.notepad.setValue(meta.notepad ?? "");
            case "created": return box.created.setValue(meta.created);
            case "modified": return box.modified.setValue(meta.modified);
        }
    }
    #parseTags(json) {
        const { status, value } = tryCatch(() => JSON.parse(json));
        return status === "success" && Array.isArray(value)
            ? value.filter((tag) => typeof tag === "string")
            : [];
    }
    static async #writeFiles({ uuid, project, meta, cover }) {
        return Promise.all([
            Workers.Opfs.write(ProjectPaths.projectFile(uuid), new Uint8Array(project.toArrayBuffer())),
            Workers.Opfs.write(ProjectPaths.projectMeta(uuid), new TextEncoder().encode(JSON.stringify(meta))),
            cover.match({
                none: () => Promise.resolve(),
                some: x => Workers.Opfs.write(ProjectPaths.projectCover(uuid), new Uint8Array(x))
            })
        ]).then(EmptyExec);
    }
}
